import { Injectable } from '@angular/core';

/**
 * Event payloads that survive a workspace tab switch.
 *
 * Alarms are stored so a returning tab can paint immediately, then the panel
 * refetches them. Site timezone and outcome catalogs are not event-scoped.
 */
export type CachedEventEntity =
  | 'detail'
  | 'cameras'
  | 'talkdowns'
  | 'alarms'
  | 'site'
  | 'raiseOutcomes'
  | 'closeOutcomes'
  | 'disarmReasons'
  | 'cameraDisarmReasons';

/** Shared key for raise/close outcomes and disarm reasons. */
export const EVENT_CACHE_CATALOG_ID = 'catalog';

const EVENT_SCOPED_ENTITIES: readonly CachedEventEntity[] = [
  'detail',
  'cameras',
  'talkdowns',
  'alarms',
];

/** Distinguishes payloads loaded for the same event under different request params (site id). */
type CacheVariant = string | number | null | undefined;

/**
 * In-memory event payloads shared across workspace tab switches.
 *
 * Switching tabs is a router navigation, so the route component and every
 * `resource()` it owns are destroyed and rebuilt. Without this cache the rebuilt
 * panel refetches everything it already had. Keyed by event id rather than tab id
 * because an event is held by exactly one tab at a time (see `EventHandleService`).
 *
 * Entries are dropped when the event leaves a tab (closed, parked, raised) or the
 * tab workspace closes — never on a timer, so a tab switch is always a cache hit.
 */
@Injectable({ providedIn: 'root' })
export class EventDataCacheService {
  private readonly entries = new Map<string, unknown>();
  private readonly inFlight = new Map<string, Promise<unknown>>();

  read<T>(entity: CachedEventEntity, eventId: string, variant?: CacheVariant): T | undefined {
    if (!eventId) {
      return undefined;
    }
    return this.entries.get(this.key(entity, eventId, variant)) as T | undefined;
  }

  write<T>(entity: CachedEventEntity, eventId: string, value: T, variant?: CacheVariant): void {
    if (!eventId) {
      return;
    }
    this.entries.set(this.key(entity, eventId, variant), value);
  }

  /** Returns the cached payload, or loads it once and stores it for later tab switches. */
  async loadCached<T>(
    entity: CachedEventEntity,
    eventId: string,
    variant: CacheVariant,
    load: () => Promise<T>,
  ): Promise<T> {
    const cached = this.read<T>(entity, eventId, variant);
    if (cached !== undefined) {
      return cached;
    }

    const cacheKey = this.key(entity, eventId, variant);
    const pending = this.inFlight.get(cacheKey);
    if (pending) {
      return pending as Promise<T>;
    }

    const request = (async () => {
      try {
        const value = await load();
        this.write(entity, eventId, value, variant);
        return value;
      } finally {
        this.inFlight.delete(cacheKey);
      }
    })();

    this.inFlight.set(cacheKey, request);
    return request;
  }

  /** Drops one entity across every variant so the next load refetches (e.g. after arm/disarm). */
  invalidate(entity: CachedEventEntity, eventId: string): void {
    if (!eventId) {
      return;
    }
    const prefix = this.key(entity, eventId, undefined);
    for (const key of this.entries.keys()) {
      if (key.startsWith(prefix)) {
        this.entries.delete(key);
      }
    }
  }

  /** Drops every event-scoped payload once it leaves a tab. */
  clearEvent(eventId: string | null | undefined): void {
    if (!eventId) {
      return;
    }
    for (const entity of EVENT_SCOPED_ENTITIES) {
      this.invalidate(entity, eventId);
    }
  }

  clear(): void {
    this.entries.clear();
    this.inFlight.clear();
  }

  private key(entity: CachedEventEntity, eventId: string, variant: CacheVariant): string {
    return `${entity}|${eventId}|${variant ?? ''}`;
  }
}
